/**
 * Company Settings: all UI strings for CompanyProfile and ContactDetails pages.
 */

export const companySettingsEn = {
  companySettings: {
    // CompanyProfile
    profile: {
      publicInfoBanner: 'Please note that the following information can be made public on your connected website or rental listing sites depending on how these fields are being used. For assistance, please contact customer support.',

      companyInformation: 'Company Information',
      labelName: 'Name',
      labelAddress: 'Address',
      labelCity: 'City',
      labelProvince: 'Province',
      labelPostal: 'Postal',
      labelPhone: 'Phone',
      labelEmail: 'Email',
      labelTimeZone: 'Time Zone',
      labelWebsiteUrl: 'Website URL',

      primaryContact: 'Primary Contact',
      primaryContactRequiredBanner: 'Primary Contact is a mandatory field. Please provide an input for your main primary contact.',
      labelFirstName: 'First Name',
      labelLastName: 'Last Name',
      labelPhoneNumber: 'Phone Number',

      logos: 'Logos',
      labelPrimaryLogo: 'Primary Logo',
      labelSecondaryLogo: 'Secondary Logo',
      btnSave: 'Save',

      socialMediaAccounts: 'Social Media Accounts',
      labelFacebook: 'Facebook',
      labelTwitter: 'Twitter',
      labelLinkedin: 'Linkedin',
      labelInstagram: 'Instagram',
      labelPinterest: 'Pinterest',
      labelYouTube: 'YouTube',

      defaultPropertyEmailSettings: 'Default Property Email Settings',
      labelContactEmails: 'Contact Email(s)',
      labelMaintenanceEmails: 'Maintenance Email(s)',
    },

    // ContactDetails
    contactDetails: {
      pageTitle: 'Update Contact Details',
      pageSubtitle: 'For Rentsync communication only.',
      pageDescription: 'From time to time, different teams at Rentsync may need to reach out to people at your company. We want to ensure those communications get to the right people. Help us out by ensuring we have the correct contact information for your team.',
      pageNote: 'Please note that the information provided below will not impact user groups or permissions on your Rentsync account, nor will they change who invoices are sent to.',

      companyOwnerTitle: 'Company Owner(s)',
      companyOwnerTooltip: 'There must be at least one contact under Company Owner(s)',
      companyOwnerDetail: 'Please add the owner(s) of your company.',

      primaryContactTitle: 'Primary Contact(s)',
      primaryContactTooltip: 'There must be at least one contact under Primary Contact(s)',
      primaryContactDetail: 'Please list our main point of contact for all general communications.',

      marketingContactTitle: 'Marketing Contact(s)',
      marketingContactTooltip: 'There must be at least one contact under Marketing Contact(s)',
      marketingContactDetail: 'Please list everyone that should receive emails from our marketing team. Marketing emails are sent sparingly and typically announce new features and services, or share informative posts.',

      emergencyContactTitle: 'Emergency Contact(s)',
      emergencyContactTooltip: 'There must be at least one contact under Emergency Contact(s)',
      emergencyContactDetail: 'Please list everyone that should be contacted in an emergency, such as a prolonged service outage.',

      itContactTitle: 'IT Contact(s)',
      itContactTooltip: 'There must be at least one contact under IT Contact(s)',
      itContactDetail: 'Please list the IT professionals at your company.',

      billingContactsTitle: 'Billing Contact(s) for Invoices',
      billingContactsDetail: 'Please list all contacts that should receive invoices.',
      billingContactsBtn: 'Update Now',
      billingContactsIframe: 'Contact Rentsync Support to update information.',

      billingProfileTitle: 'Billing Profile Information',
      billingProfileDetail: 'Please update your billing profile information.',
      billingProfileBtn: 'Update Now',
      billingProfileIframe: 'Contact Rentsync Support to update information.',

      unsavedChangesTitle: 'You have unsaved changes.',
      saveBtnTooltip: 'Please check the checkbox to confirm that no more updates are needed to enable the \'Save\' button.',
      checkboxText: 'Content reviewed, no more updates needed.',
      toastFormInvalid: 'Form is invalid',
      toastContactLimitExceeded: 'Exceeding contact limit. Please delete extra contacts.',
      toastChangesSaved: 'Changes saved!',
      toastChangesSavedTitle: 'Success!',
      toastSupportError: 'Please report this issue to support.',
      toastContactDeleted: 'Contact deleted successfully.',
      toastErrorTitle: 'Error',
    },

    // RsModalSettings
    modal: {
      btnUpdate: 'Update Profile',
      title: 'Company Profile',

      tabGeneral: 'General',
      labelCompanyName: 'Company Name',
      labelWebsiteUrl: 'Website URL',
      labelStreetAddress: 'Street Address',
      labelAddressLine2: 'Address Line 2',
      labelCity: 'City',
      placeholderCitySearch: 'Search city by name...',
      labelPostalZip: 'Postal/Zip',
      labelPhone: 'Phone',
      labelEmail: 'Email',
      labelFax: 'Fax',
      labelTimezone: 'Timezone',
      timezoneSelect: 'Select a timezone',

      tabPrimaryContact: 'Primary Contact',
      primaryContactCardDetail: 'Please list your main point of contact for all general communication.',
      primaryContactLinkText: 'If you would like to view or update all your contact details, click here:',
      primaryContactLinkLabel: 'Go To Contact Details',
      primaryContactSaveWarning: 'Please save your changes before navigating to the Contact Details page',

      tabSocialLinks: 'Social Links',
      labelFacebook: 'Facebook',
      labelLinkedIn: 'LinkedIn',
      labelTwitter: 'Twitter',
      labelInstagram: 'Instagram',
      labelPinterest: 'Pinterest',
      labelYouTube: 'YouTube',

      tabDefaultPropertyEmails: 'Default Property Emails',
      emailSettingsInfo: 'These are default emails addresses used when new properties are created. Changing these values does not override any property\'s current values.',
      emailSettingsContactNote: '* Contact email addresses receive property inquiries. Separate each email address on a new line.',
      emailSettingsMaintenanceNote: '* Maintenance email addresses receive property maintenance requests. Separate each email address on a new line.',
      labelContactEmails: 'Contact Email Address(es)',
      labelMaintenanceEmails: 'Maintenance Email Address(es)',

      toastValidationError: 'You have some errors! Please fix before saving.',
    },

    // RsPartialContactCard
    contactCard: {
      contactLabel: 'Contact {n}',
      btnAddContact: 'Add Contact',
      deleteTitle: 'Delete Contact',
      deleteText: 'Are you sure you want to delete this contact?',
    },

    // RsContactInfo
    contactInfo: {
      labelFirstName: 'First Name',
      labelLastName: 'Last Name',
      labelEmail: 'E-mail',
      labelPrimaryPhone: 'Primary Phone Number',
    },
  },
} as const;

export const companySettingsFr = {
  companySettings: {
    // CompanyProfile
    profile: {
      publicInfoBanner: 'Veuillez noter que les informations suivantes peuvent être rendues publiques sur votre site web connecté ou sur les sites d\'annonces de location selon la façon dont ces champs sont utilisés. Pour obtenir de l\'aide, veuillez contacter le support client.',

      companyInformation: 'Informations sur l\'entreprise',
      labelName: 'Nom',
      labelAddress: 'Adresse',
      labelCity: 'Ville',
      labelProvince: 'Province',
      labelPostal: 'Code postal',
      labelPhone: 'Téléphone',
      labelEmail: 'Courriel',
      labelTimeZone: 'Fuseau horaire',
      labelWebsiteUrl: 'URL du site web',

      primaryContact: 'Contact principal',
      primaryContactRequiredBanner: 'Le contact principal est un champ obligatoire. Veuillez fournir un contact principal.',
      labelFirstName: 'Prénom',
      labelLastName: 'Nom de famille',
      labelPhoneNumber: 'Numéro de téléphone',

      logos: 'Logos',
      labelPrimaryLogo: 'Logo principal',
      labelSecondaryLogo: 'Logo secondaire',
      btnSave: 'Enregistrer',

      socialMediaAccounts: 'Comptes de réseaux sociaux',
      labelFacebook: 'Facebook',
      labelTwitter: 'Twitter',
      labelLinkedin: 'Linkedin',
      labelInstagram: 'Instagram',
      labelPinterest: 'Pinterest',
      labelYouTube: 'YouTube',

      defaultPropertyEmailSettings: 'Paramètres de courriel par défaut de la propriété',
      labelContactEmails: 'Courriel(s) de contact',
      labelMaintenanceEmails: 'Courriel(s) de maintenance',

      adSyndicationLogos: 'Logos de syndication des annonces [Superadmin seulement]',
      labelSmallLogoUrl: 'URL du petit logo',
      labelMediumLogoUrl: 'URL du logo moyen',
      labelLargeLogoUrl: 'URL du grand logo',
    },

    // ContactDetails
    contactDetails: {
      pageTitle: 'Mettre à jour les coordonnées',
      pageSubtitle: 'Pour les communications internes de Rentsync uniquement.',
      pageDescription: 'De temps à autre, différentes équipes chez Rentsync peuvent avoir besoin de contacter des personnes dans votre entreprise. Nous voulons nous assurer que ces communications parviennent aux bonnes personnes. Aidez-nous en vous assurant que nous disposons des coordonnées correctes pour votre équipe.',
      pageNote: 'Veuillez noter que les informations fournies ci-dessous n\'auront aucune incidence sur les groupes d\'utilisateurs ou les autorisations de votre compte Rentsync, ni ne changeront à qui les factures sont envoyées.',

      companyOwnerTitle: 'Propriétaire(s) de l\'entreprise',
      companyOwnerTooltip: 'Il doit y avoir au moins un contact sous Propriétaire(s) de l\'entreprise',
      companyOwnerDetail: 'Veuillez ajouter le(s) propriétaire(s) de votre entreprise.',

      primaryContactTitle: 'Contact(s) principal/principaux',
      primaryContactTooltip: 'Il doit y avoir au moins un contact sous Contact(s) principal/principaux',
      primaryContactDetail: 'Veuillez indiquer notre principal point de contact pour toutes les communications générales.',

      marketingContactTitle: 'Contact(s) marketing',
      marketingContactTooltip: 'Il doit y avoir au moins un contact sous Contact(s) marketing',
      marketingContactDetail: 'Veuillez lister toutes les personnes qui doivent recevoir des courriels de notre équipe marketing. Les courriels marketing sont envoyés avec parcimonie et annoncent généralement de nouvelles fonctionnalités et services, ou partagent des articles informatifs.',

      emergencyContactTitle: 'Contact(s) d\'urgence',
      emergencyContactTooltip: 'Il doit y avoir au moins un contact sous Contact(s) d\'urgence',
      emergencyContactDetail: 'Veuillez lister toutes les personnes à contacter en cas d\'urgence, comme une interruption prolongée du service.',

      itContactTitle: 'Contact(s) informatique',
      itContactTooltip: 'Il doit y avoir au moins un contact sous Contact(s) informatique',
      itContactDetail: 'Veuillez lister les professionnels informatiques de votre entreprise.',

      billingContactsTitle: 'Contact(s) de facturation pour les factures',
      billingContactsDetail: 'Veuillez lister tous les contacts qui doivent recevoir les factures.',
      billingContactsBtn: 'Mettre à jour maintenant',
      billingContactsIframe: 'Contactez le support Rentsync pour mettre à jour les informations.',

      billingProfileTitle: 'Informations du profil de facturation',
      billingProfileDetail: 'Veuillez mettre à jour les informations de votre profil de facturation.',
      billingProfileBtn: 'Mettre à jour maintenant',
      billingProfileIframe: 'Contactez le support Rentsync pour mettre à jour les informations.',

      unsavedChangesTitle: 'Vous avez des modifications non enregistrées.',
      saveBtnTooltip: 'Veuillez cocher la case pour confirmer qu\'aucune autre mise à jour n\'est nécessaire afin d\'activer le bouton \'Enregistrer\'.',
      checkboxText: 'Contenu révisé, aucune autre mise à jour nécessaire.',
      toastFormInvalid: 'Le formulaire est invalide',
      toastContactLimitExceeded: 'Limite de contacts dépassée. Veuillez supprimer les contacts en excès.',
      toastChangesSaved: 'Modifications enregistrées !',
      toastChangesSavedTitle: 'Succès !',
      toastSupportError: 'Veuillez signaler ce problème au support.',
      toastContactDeleted: 'Contact supprimé avec succès.',
      toastErrorTitle: 'Erreur',
    },

    // RsModalSettings
    modal: {
      btnUpdate: 'Mettre à jour le profil',
      title: 'Profil de l\'entreprise',

      tabGeneral: 'Général',
      labelCompanyName: 'Nom de l\'entreprise',
      labelWebsiteUrl: 'URL du site web',
      labelStreetAddress: 'Adresse',
      labelAddressLine2: 'Adresse ligne 2',
      labelCity: 'Ville',
      placeholderCitySearch: 'Rechercher une ville par nom...',
      labelPostalZip: 'Code postal',
      labelPhone: 'Téléphone',
      labelEmail: 'Courriel',
      labelFax: 'Fax',
      labelTimezone: 'Fuseau horaire',
      timezoneSelect: 'Sélectionner un fuseau horaire',

      tabPrimaryContact: 'Contact principal',
      primaryContactCardDetail: 'Veuillez indiquer votre principal point de contact pour toutes les communications générales.',
      primaryContactLinkText: 'Si vous souhaitez consulter ou mettre à jour toutes vos coordonnées, cliquez ici :',
      primaryContactLinkLabel: 'Accéder aux coordonnées',
      primaryContactSaveWarning: 'Veuillez enregistrer vos modifications avant de naviguer vers la page des coordonnées',

      tabSocialLinks: 'Liens sociaux',
      labelFacebook: 'Facebook',
      labelLinkedIn: 'LinkedIn',
      labelTwitter: 'Twitter',
      labelInstagram: 'Instagram',
      labelPinterest: 'Pinterest',
      labelYouTube: 'YouTube',

      tabDefaultPropertyEmails: 'Courriels de propriété par défaut',
      emailSettingsInfo: 'Ce sont les adresses courriel par défaut utilisées lors de la création de nouvelles propriétés. La modification de ces valeurs ne remplace pas les valeurs actuelles d\'une propriété.',
      emailSettingsContactNote: '* Les adresses courriel de contact reçoivent les demandes de renseignements sur les propriétés. Séparez chaque adresse courriel sur une nouvelle ligne.',
      emailSettingsMaintenanceNote: '* Les adresses courriel de maintenance reçoivent les demandes de maintenance des propriétés. Séparez chaque adresse courriel sur une nouvelle ligne.',
      labelContactEmails: 'Adresse(s) courriel de contact',
      labelMaintenanceEmails: 'Adresse(s) courriel de maintenance',

      tabAdSyndicationLogos: 'Logos de syndication des annonces',
      labelSmallLogoUrl: 'URL du petit logo',
      labelMediumLogoUrl: 'URL du logo moyen',
      labelLargeLogoUrl: 'URL du grand logo',

      toastValidationError: 'Vous avez des erreurs ! Veuillez les corriger avant d\'enregistrer.',
    },

    // RsPartialContactCard
    contactCard: {
      contactLabel: 'Contact {n}',
      btnAddContact: 'Ajouter un contact',
      deleteTitle: 'Supprimer le contact',
      deleteText: 'Êtes-vous sûr de vouloir supprimer ce contact ?',
    },

    // RsContactInfo
    contactInfo: {
      labelFirstName: 'Prénom',
      labelLastName: 'Nom de famille',
      labelEmail: 'Courriel',
      labelPrimaryPhone: 'Numéro de téléphone principal',
    },
  },
} as const;
